import { useState, useEffect } from 'react';
import { useFeedback } from '../common/Feedback';
import { Eye, EyeOff, Building, Copy, Check, Pencil, Trash2 } from 'lucide-react';
import { RowMenu } from '../common/RowMenu';
import { Credential } from '../../hooks/useCredentials';
import { useAuth } from '../../hooks/useAuth';
import { apiClient } from '../../api/client';
import { CodigoDoServidor } from './CodigoDoServidor';
import { generateTotpCode, getTotpRemainingSeconds } from '../../utils/qr';
import { useCatalog } from '../../hooks/useCatalog';
import { courtLabel, subtituloSemRepetir } from '../../utils/catalog';
import { AvisoInstalarExtensao, BotaoAcessar, useAcessar } from './BotaoAcessar';

interface CredentialListRowProps {
  credential: Credential;
  onEdit: (c: Credential) => void;
  onDelete: (id: string) => void;
  selectionMode?: boolean;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
  /** Pasta do titular, mostrada nos resultados da busca na visão Pastas (Etapa 10a). */
  folderLabel?: string;
}

export function CredentialListRow({ credential, onEdit, onDelete, selectionMode = false, selected = false, onToggleSelect, folderLabel }: CredentialListRowProps) {
  const { avisar } = useFeedback();
  const { membership, user } = useAuth();
  const isAdmin = membership?.role === 'admin' || membership?.role === 'owner';
  const isCreator = credential.created_by === user?.id;
  const canManage = isAdmin || isCreator;
  const { catalog } = useCatalog();
  // M2: o subtítulo só traz o que o título ainda não diz.
  const tribunalLabel = subtituloSemRepetir(credential.label || '', courtLabel(catalog, credential.catalog_key));
  // D23: "Acessar" (já logado pela extensão; sem ela, abre a página de login e mostra o aviso).
  const acessar = useAcessar(credential);
  const [revealed, setRevealed] = useState<{ login?: string; password?: string; totp?: string } | null>(null);
  const [isRevealing, setIsRevealing] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  // Live TOTP code generated CLIENT-SIDE
  const [liveCode, setLiveCode] = useState('');
  const [liveTimeLeft, setLiveTimeLeft] = useState(30);

  useEffect(() => {
    const secret = credential.totp_secret;
    if (!secret) {
      setLiveCode('');
      return;
    }

    const update = () => {
      setLiveCode(generateTotpCode(secret));
      setLiveTimeLeft(getTotpRemainingSeconds(30));
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [credential.totp_secret]);

  const handleReveal = async () => {
    if (revealed) {
      setRevealed(null);
      return;
    }
    
    setIsRevealing(true);
    try {
      const data = await apiClient.get<any>(`/credentials/${credential.id}?reveal=true`);
      setRevealed({ login: data.login, password: data.password, totp: data.totp_secret });
    } catch (e) {
      console.error('Failed to reveal credential');
    } finally {
      setIsRevealing(false);
    }
  };

  const fallbackCopyToClipboard = (text: string, field: string) => {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.top = '0';
      textArea.style.left = '0';
      textArea.style.position = 'fixed';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      if (successful) {
        setCopied(field);
        setTimeout(() => setCopied(null), 2000);
      } else {
        avisar('Não foi possível copiar automaticamente. Por favor, copie manualmente.', 'erro');
      }
    } catch (err) {
      console.error('Fallback copy failed', err);
      avisar('Falha ao copiar credencial.', 'erro');
    }
  };

  const copyToClipboard = (text: string, field: string) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text)
        .then(() => {
          setCopied(field);
          setTimeout(() => setCopied(null), 2000);
        })
        .catch(err => {
          console.error('Clipboard write failed, trying fallback', err);
          fallbackCopyToClipboard(text, field);
        });
    } else {
      fallbackCopyToClipboard(text, field);
    }
  };

  const handleCopy = async (field: 'login' | 'password' | 'totpCode') => {
    if (revealed) {
      if (field === 'login' && revealed.login) copyToClipboard(revealed.login, 'login');
      if (field === 'password' && revealed.password) copyToClipboard(revealed.password, 'password');
      if (field === 'totpCode' && liveCode) copyToClipboard(liveCode, 'totpCode');
      return;
    }
    
    try {
      const data = await apiClient.post<any>(`/credentials/${credential.id}/use`);
      if (field === 'login') copyToClipboard(data.login, 'login');
      if (field === 'password') copyToClipboard(data.password, 'password');
      if (field === 'totpCode') {
        const code = data.totp_code || '';
        copyToClipboard(code, 'totpCode');
      }
    } catch (e) {
      console.error('Failed to copy credential', e);
      avisar('Falha ao copiar credencial.', 'erro');
    }
  };

  const maskString = (_str: string) => '•'.repeat(8);

  return (
    <div className="glass animate-scale-in" style={{
      borderRadius: 'var(--radius-lg)',
      padding: 'var(--space-5) var(--space-6)',
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--space-3)',
      minWidth: 0,
      boxShadow: 'var(--shadow-md)',
      transition: 'transform var(--duration-base) var(--ease-out), border-color var(--duration-base)',
    }}
    onMouseOver={(e) => {
      e.currentTarget.style.borderColor = 'rgba(26, 154, 102, 0.3)';
    }}
    onMouseOut={(e) => {
      e.currentTarget.style.borderColor = 'var(--color-border-default)';
    }}
    >
      {/* Row 1: Header with label, actions, and reveal */}
      {/* D23: no celular, os botões descem para a linha de baixo em vez de passar da tela. */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--space-3) var(--space-4)', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flex: '1 1 auto', minWidth: 0 }}>
          {selectionMode && canManage && (
            <input
              type="checkbox"
              checked={selected}
              onChange={() => onToggleSelect?.(credential.id)}
              style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: 'var(--color-primary-500, #10b981)', flexShrink: 0 }}
              title="Selecionar para atualização em massa"
            />
          )}
          <div style={{ padding: 'var(--space-2)', background: 'var(--color-bg-elevated)', borderRadius: 'var(--radius-md)', flexShrink: 0 }}>
            <Building size={18} color="var(--color-primary-400)" />
          </div>
          <h3 title={credential.label} style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{credential.label}</h3>
          {tribunalLabel && <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 1 }}>{tribunalLabel}</span>}
          {folderLabel && <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-primary-400)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 1, minWidth: 0 }}>Pasta: {folderLabel}</span>}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexShrink: 0, flexWrap: 'wrap' }}>
          {/* D23: "Acessar" primário compacto, sempre visível (substitui o "Abrir" e o ícone "Acessar sistema"). */}
          {acessar.ativo && <BotaoAcessar credentialId={credential.id} acao={acessar.acao} compacto />}

          {/* Reveal button */}
          {canManage && (
            <button 
              onClick={handleReveal} 
              disabled={isRevealing}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 'var(--space-1)', 
                background: 'transparent', 
                border: '1px solid var(--color-border-default)', 
                color: 'var(--color-primary-400)', 
                cursor: 'pointer', 
                fontSize: 'var(--text-xs)',
                padding: '4px 10px',
                borderRadius: 'var(--radius-md)',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.borderColor = 'var(--color-primary-400)';
                e.currentTarget.style.background = 'rgba(26, 154, 102, 0.06)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.borderColor = 'var(--color-border-default)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              {revealed ? <EyeOff size={14} /> : <Eye size={14} />}
              {isRevealing ? 'Aguarde...' : (revealed ? 'Ocultar' : 'Revelar')}
            </button>
          )}

          {/* M3: editar à mão; excluir no menu ⋯ com confirmação. B1: alvos de toque de 40 px. */}
          {canManage && (
            <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
              <button type="button" className="botao-icone" onClick={() => onEdit(credential)} title="Editar credencial" aria-label={`Editar ${credential.label}`}>
                <Pencil size={14} />
              </button>
              <RowMenu
                rotulo={`Mais ações de ${credential.label}`}
                itens={[{ rotulo: 'Excluir credencial', icone: <Trash2 size={16} />, aoClicar: () => onDelete(credential.id), perigo: true }]}
              />
            </div>
          )}
        </div>
      </div>

      {/* Row 2: Credentials data (shown when revealed or masked) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)', flexWrap: 'wrap' }}>
        {/* Login */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', minWidth: 0 }}>
          <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-xs)', fontWeight: '500' }}>Login:</span>
          <code style={{ background: 'var(--color-bg-root)', padding: '2px 8px', borderRadius: '4px', fontSize: 'var(--text-xs)', color: 'var(--color-text-primary)', minWidth: 0, overflowWrap: 'anywhere' }}>
            {revealed?.login || credential.login_masked || maskString('user')}
          </code>
          <button 
            onClick={() => handleCopy('login')} 
            type="button"
            className="botao-icone botao-icone--compacto botao-icone--sem-borda"
            title="Copiar login"
          >
            {copied === 'login' ? <Check size={13} color="var(--color-primary-400)" /> : <Copy size={13} />}
          </button>
        </div>

        {/* Password */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', minWidth: 0 }}>
          <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-xs)', fontWeight: '500' }}>Senha:</span>
          <code style={{ background: 'var(--color-bg-root)', padding: '2px 8px', borderRadius: '4px', fontSize: 'var(--text-xs)', color: 'var(--color-text-primary)', minWidth: 0, overflowWrap: 'anywhere' }}>
            {revealed?.password || credential.password_masked || maskString('pass')}
          </code>
          <button 
            onClick={() => handleCopy('password')} 
            type="button"
            className="botao-icone botao-icone--compacto botao-icone--sem-borda"
            title="Copiar senha"
          >
            {copied === 'password' ? <Check size={13} color="var(--color-primary-400)" /> : <Copy size={13} />}
          </button>
        </div>

        {/* TOTP Code */}
        {credential.totp_secret && liveCode && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <span title="O código 2FA aparece sem precisar revelar porque muda a cada 30 segundos e, sozinho, não dá acesso: ainda é preciso o login e a senha." style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-xs)', fontWeight: '500', cursor: 'help' }}>2FA:</span>
            <code style={{
              background: 'var(--color-bg-root)',
              padding: '2px 8px',
              borderRadius: '4px',
              fontSize: 'var(--text-xs)',
              color: liveTimeLeft <= 5 ? 'var(--color-error, #ef4444)' : 'var(--color-primary-400)',
              fontWeight: 'bold',
              letterSpacing: '1.5px',
              fontFamily: 'monospace',
              transition: 'color 0.3s ease'
            }}>
              {liveCode.slice(0, 3)} {liveCode.slice(3)}
            </code>
            {/* Inline circular countdown */}
            <div style={{ position: 'relative', width: '20px', height: '20px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="20" height="20" viewBox="0 0 36 36" style={{ transform: 'rotate(-90deg)' }}>
                <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="4" />
                <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none"
                  stroke={liveTimeLeft <= 5 ? 'var(--color-error, #ef4444)' : 'var(--color-primary-500, #10b981)'}
                  strokeWidth="4"
                  strokeDasharray={`${(liveTimeLeft / 30) * 100}, 100`}
                  style={{ transition: 'stroke-dasharray 1s linear, stroke 0.3s ease' }}
                />
              </svg>
              <span style={{ position: 'absolute', fontSize: '8px', fontWeight: 'bold', color: 'var(--color-text-primary)' }}>{liveTimeLeft}</span>
            </div>
            <button 
              onClick={() => handleCopy('totpCode')} 
              type="button"
            className="botao-icone botao-icone--compacto botao-icone--sem-borda"
            title="Copiar código 2FA"
            >
              {copied === 'totpCode' ? <Check size={13} color="var(--color-primary-400)" /> : <Copy size={13} />}
            </button>
          </div>
        )}

        {!credential.totp_secret && credential.has_totp && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-xs)', fontWeight: '500' }}>2FA:</span>
            <CodigoDoServidor credentialId={credential.id} compacto />
          </div>
        )}
      </div>

      {/* D23: sem a extensão, o aviso (texto aprovado) com o link para instalar. */}
      {acessar.mostrarAviso && <AvisoInstalarExtensao />}
    </div>
  );
}
